import { useEffect, useRef } from 'react';
import { AudioVisualizationData } from '../types/voice';

interface AudioVisualizerProps {
  data?: AudioVisualizationData | null;
  type?: 'waveform' | 'frequency' | 'bars';
  color?: string;
  backgroundColor?: string;
  width?: number;
  height?: number;
  className?: string;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  data,
  type = 'bars',
  color = 'var(--color-primary-600)',
  backgroundColor = 'var(--color-bg-secondary)',
  width = 300,
  height = 100,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);

      if (!data) {
        // Draw idle state
        drawIdleState(ctx);
      } else {
        switch (type) {
          case 'waveform':
            drawWaveform(ctx, data);
            break;
          case 'frequency':
            drawFrequency(ctx, data);
            break;
          case 'bars':
          default:
            drawBars(ctx, data);
            break;
        }
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [data, type, color, backgroundColor, width, height]);

  const drawIdleState = (ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();
  };

  const drawWaveform = (ctx: CanvasRenderingContext2D, data: AudioVisualizationData) => {
    const waveform = data.waveform || [];
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();

    const sliceWidth = width / waveform.length;
    let x = 0;

    for (let i = 0; i < waveform.length; i++) {
      const v = waveform[i];
      const y = (v + 1) / 2 * height;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.stroke();
  };

  const drawFrequency = (ctx: CanvasRenderingContext2D, data: AudioVisualizationData) => {
    const frequency = data.frequency || [];
    const barWidth = (width / frequency.length) * 0.8;
    const barGap = (width / frequency.length) * 0.2;

    ctx.fillStyle = color;

    frequency.forEach((value, index) => {
      const barHeight = (value / 255) * height;
      const x = index * (barWidth + barGap);
      const y = height - barHeight;

      // Create gradient
      const gradient = ctx.createLinearGradient(0, y, 0, height);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, color + '33');
      ctx.fillStyle = gradient;

      ctx.fillRect(x, y, barWidth, barHeight);
    });
  };

  const drawBars = (ctx: CanvasRenderingContext2D, data: AudioVisualizationData) => {
    const numBars = 32;
    const barWidth = width / numBars;
    const barSpacing = 2;
    
    // Use frequency data or generate from volume
    const values = data.frequency?.slice(0, numBars) || 
      Array(numBars).fill(0).map(() => data.volume * 255 * (0.5 + Math.random() * 0.5));

    values.forEach((value, index) => {
      const barHeight = (value / 255) * height * 0.8;
      const x = index * barWidth + barSpacing;
      const y = (height - barHeight) / 2;

      // Create gradient
      const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, color + '66');
      ctx.fillStyle = gradient;

      ctx.fillRect(x, y, barWidth - barSpacing * 2, barHeight);
    });
  };

  return (
    <canvas
      ref={canvasRef}
      className={`${className}`}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
};

interface WaveformVisualizerProps {
  isActive: boolean;
  amplitude?: number;
  className?: string;
}

export const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
  isActive,
  amplitude = 0,
  className = ''
}) => {
  return (
    <div className={`flex items-center justify-center gap-1 ${className}`}>
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="w-1 bg-current transition-all duration-150"
          style={{
            height: isActive 
              ? `${Math.max(8, Math.random() * 20 + amplitude * 20)}px`
              : '4px',
            backgroundColor: isActive 
              ? 'var(--color-primary-600)'
              : 'var(--color-text-tertiary)',
            opacity: isActive ? 0.8 + Math.random() * 0.2 : 0.3
          }}
        />
      ))}
    </div>
  );
};

interface CircularVisualizerProps {
  isActive: boolean;
  level: number;
  size?: number;
  className?: string;
}

export const CircularVisualizer: React.FC<CircularVisualizerProps> = ({
  isActive,
  level,
  size = 120,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 3;

    const draw = () => {
      ctx.clearRect(0, 0, size, size);

      if (isActive) {
        // Draw multiple circles with different sizes based on level
        for (let i = 0; i < 3; i++) {
          const r = radius + (level * 50 * (i + 1));
          const opacity = 0.3 - (i * 0.1);
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, r, 0, 2 * Math.PI);
          ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      // Draw center circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fillStyle = isActive ? 'var(--color-primary-600)' : 'var(--color-text-tertiary)';
      ctx.fill();

      // Draw inner icon (microphone shape)
      ctx.fillStyle = 'white';
      ctx.fillRect(centerX - 6, centerY - 20, 12, 20);
      ctx.beginPath();
      ctx.arc(centerX, centerY - 20, 6, Math.PI, 0);
      ctx.fill();
      ctx.fillRect(centerX - 2, centerY, 4, 10);
      ctx.fillRect(centerX - 10, centerY + 8, 20, 4);
    };

    const animate = () => {
      draw();
      if (isActive) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [isActive, level, size]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: `${size}px`, height: `${size}px` }}
    />
  );
};